import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AdminControls = () => {
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showErrorLog, setShowErrorLog] = useState(false);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    // Simulate refresh process
    setTimeout(() => {
      setIsRefreshing(false);
    }, 3000);
  };

  const errorLogs = [
    {
      id: 1,
      timestamp: '2025-01-05 14:23:15',
      level: 'warning',
      message: 'Duplicate booking number detected: BK2025010501',
      source: 'CSV Parser'
    },
    {
      id: 2,
      timestamp: '2025-01-04 09:15:42',
      level: 'error',
      message: 'Invalid date format in row 47: 01/32/2025',
      source: 'Data Validator'
    },
    {
      id: 3,
      timestamp: '2025-01-03 16:08:33',
      level: 'info',
      message: 'Database connection restored after temporary outage',
      source: 'Database Manager'
    }
  ];

  const getLogLevelColor = (level) => {
    switch (level) {
      case 'error': return 'text-error';
      case 'warning': return 'text-warning';
      case 'info': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getLogLevelIcon = (level) => {
    switch (level) {
      case 'error': return 'XCircle';
      case 'warning': return 'AlertTriangle';
      case 'info': return 'Info';
      default: return 'Circle';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Administrative Controls</h3>
        <div className="flex items-center space-x-2">
          <div className="w-2 h-2 bg-primary rounded-full"></div>
          <span className="text-sm text-primary font-medium">Admin Access</span>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Button
          variant="outline"
          onClick={handleManualRefresh}
          loading={isRefreshing}
          iconName="RefreshCw"
          iconPosition="left"
          className="w-full"
        >
          {isRefreshing ? 'Refreshing Database...' : 'Manual Database Refresh'}
        </Button>

        <Button
          variant="secondary"
          onClick={() => setShowErrorLog(!showErrorLog)}
          iconName="FileText"
          iconPosition="left"
          className="w-full"
        >
          {showErrorLog ? 'Hide Error Log' : 'View Error Log'}
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="p-4 bg-muted rounded-lg text-center">
          <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <Icon name="CheckCircle" size={20} className="text-accent" />
          </div>
          <p className="text-sm font-medium text-foreground">System Health</p>
          <p className="text-xs text-accent">Optimal</p>
        </div>

        <div className="p-4 bg-muted rounded-lg text-center">
          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <Icon name="Zap" size={20} className="text-primary" />
          </div>
          <p className="text-sm font-medium text-foreground">API Status</p>
          <p className="text-xs text-primary">Connected</p>
        </div>

        <div className="p-4 bg-muted rounded-lg text-center">
          <div className="w-10 h-10 bg-warning/10 rounded-full flex items-center justify-center mx-auto mb-2">
            <Icon name="Clock" size={20} className="text-warning" />
          </div>
          <p className="text-sm font-medium text-foreground">Next Sync</p>
          <p className="text-xs text-warning">6:00 AM PST</p>
        </div>
      </div>
      {showErrorLog && (
        <div className="border border-border rounded-lg p-4 bg-muted/50">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-foreground">Recent Error Log</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowErrorLog(false)}
            >
              <Icon name="X" size={16} />
            </Button>
          </div>
          
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {errorLogs?.map((log) => (
              <div key={log?.id} className="p-3 bg-card rounded border border-border">
                <div className="flex items-start space-x-2">
                  <Icon 
                    name={getLogLevelIcon(log?.level)} 
                    size={14} 
                    className={`mt-0.5 ${getLogLevelColor(log?.level)}`} 
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs font-medium uppercase ${getLogLevelColor(log?.level)}`}>
                        {log?.level}
                      </span>
                      <span className="text-xs text-muted-foreground">{log?.timestamp}</span>
                    </div>
                    <p className="text-xs text-foreground">{log?.message}</p>
                    <p className="text-xs text-muted-foreground mt-1">Source: {log?.source}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-4 p-3 bg-primary/10 rounded-lg border border-primary/20">
        <div className="flex items-start space-x-2">
          <Icon name="Shield" size={16} className="text-primary mt-0.5" />
          <div className="text-xs text-primary">
            <p className="font-medium">Security Notice:</p>
            <p className="mt-1">All administrative actions are logged and monitored. Unauthorized access attempts will be reported to system administrators.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminControls;